import React from 'react';

// --- ICONS (Heroicons SVGs) ---
// We can define them here to keep the component self-contained
const IconSuccess = () => (
  <svg className="h-6 w-6 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconError = () => (
  <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const IconInfo = () => (
  <svg className="h-6 w-6 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CloseButtonIcon = () => (
  <svg className="h-5 w-5 text-gray-400 hover:text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);


/**
 * A reusable component to display notices (e.g., success, error, info).
 * This version is styled as an "elevated" white card with a shadow.
 *
 * @param {object} props
 * @param {'success' | 'error' | 'info'} props.type - The type of notice, which determines its icon and color.
 * @param {string} [props.className] - Additional classes to apply to the notice.
 * @param {Function} [props.onClose] - If provided, shows a close button and calls this function on click.
 * @param {React.ReactNode} props.children - The message content to display.
 */
export default function Notice({ type = 'info', children, className = '', onClose }) {
  
  // 1. Set the base style: a white, rounded, shadowed card.
  const baseClasses = 'w-full bg-white rounded-xl shadow-lg flex p-4';
  
  let Icon;
  let textColor = 'text-gray-700'; // Default text

  // 2. Select the correct icon and text color based on type.
  switch (type) {
    case 'success':
      Icon = IconSuccess;
      textColor = 'text-green-800';
      break;
    case 'error':
      Icon = IconError;
      textColor = 'text-red-800';
      break;
    case 'info':
    default:
      Icon = IconInfo;
      textColor = 'text-blue-800';
      break;
  }

  return (
    <div className={`${baseClasses} ${className}`} role="alert">
      {/* Icon */}
      <div className="flex-shrink-0">
        <Icon />
      </div>

      {/* Message Text */}
      <div className="ml-3 flex-grow">
        <p className={`text-sm font-medium ${textColor}`}>
          {children}
        </p>
      </div>

      {/* Close Button (only if onClose is provided) */}
      {onClose && (
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={onClose}
            type="button"
            className="-mr-1 -my-1 p-1.5 rounded-md inline-flex focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
            aria-label="Dismiss message"
          >
            <CloseButtonIcon />
          </button>
        </div>
      )}
    </div>
  );
}

